import React, { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react';
import seedCourseData from './courseData.json';
import './App.css';

type BlockType = 'heading' | 'text' | 'description' | 'list' | 'code' | 'image' | 'file' | 'link' | 'table';

type ContentBlock = {
  id: string;
  type: BlockType;
  level?: 2 | 3 | 4;
  text?: string;
  style?: 'ul' | 'ol';
  items?: string[];
  code?: string;
  src?: string;
  alt?: string;
  caption?: string;
  href?: string;
  label?: string;
  rows?: string[][];
};

type Lesson = {
  id: string;
  title: string;
  html: string;
  blocks?: ContentBlock[];
};

type Module = {
  id: string;
  title: string;
  introHtml: string;
  introBlocks?: ContentBlock[];
  lessons: Lesson[];
};

type CourseData = {
  title: string;
  subtitle: string;
  modules: Module[];
};

const seedCourse = seedCourseData as CourseData;

const blockLabels: Record<BlockType, string> = {
  heading: 'Заголовок',
  text: 'Текст',
  description: 'Описание',
  list: 'Список',
  code: 'Код',
  image: 'Изображение',
  file: 'Файл',
  link: 'Ссылка',
  table: 'Таблица',
};

const cloneCourse = (course: CourseData) => JSON.parse(JSON.stringify(course)) as CourseData;

function blockId(prefix = 'block') {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function decodeHtmlEntities(value: string) {
  return value
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
}

function escapeHtml(value = '') {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function protectCodeBlocks(html: string) {
  return html.replace(
    /(<pre\b[^>]*>\s*<code\b[^>]*>)([\s\S]*?)(<\/code>\s*<\/pre>)/gi,
    (_match, opening, code, closing) => `${opening}${escapeHtml(decodeHtmlEntities(code))}${closing}`,
  );
}

function blocksToHtml(blocks?: ContentBlock[], fallbackHtml = '') {
  if (!blocks?.length) {
    return fallbackHtml;
  }

  return blocks.map((block) => {
    switch (block.type) {
      case 'heading': {
        const level = block.level || 3;
        return `<h${level}>${escapeHtml(block.text || '')}</h${level}>`;
      }
      case 'text':
        return `<p>${escapeHtml(block.text || '')}</p>`;
      case 'description':
        return `<blockquote>${escapeHtml(block.text || '')}</blockquote>`;
      case 'list': {
        const tag = block.style === 'ol' ? 'ol' : 'ul';
        return `<${tag}>${(block.items || []).map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</${tag}>`;
      }
      case 'code':
        return `<pre><code>${escapeHtml(block.code || '')}</code></pre>`;
      case 'image': {
        const image = `<img src="${escapeHtml(block.src || '')}" alt="${escapeHtml(block.alt || '')}">`;
        return block.caption ? `<figure>${image}<figcaption>${escapeHtml(block.caption)}</figcaption></figure>` : `<p>${image}</p>`;
      }
      case 'file':
      case 'link':
        return `<p><a href="${escapeHtml(block.href || '')}" target="_blank" rel="noopener">${escapeHtml(block.label || block.href || '')}</a></p>`;
      case 'table':
        return `<table><tbody>${(block.rows || []).map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`).join('')}</tbody></table>`;
      default:
        return '';
    }
  }).join('\n');
}

function syncHtmlFields(course: CourseData) {
  const next = cloneCourse(course);
  for (const module of next.modules) {
    module.introHtml = blocksToHtml(module.introBlocks, module.introHtml);
    for (const lesson of module.lessons) {
      lesson.html = blocksToHtml(lesson.blocks, lesson.html);
    }
  }
  return next;
}

function createBlock(type: BlockType): ContentBlock {
  const id = blockId(type);
  switch (type) {
    case 'heading':
      return { id, type, level: 3, text: 'Новый заголовок' };
    case 'text':
      return { id, type, text: 'Новый текст' };
    case 'description':
      return { id, type, text: 'Описание или примечание' };
    case 'list':
      return { id, type, style: 'ul', items: ['Первый пункт'] };
    case 'code':
      return { id, type, code: '# команды' };
    case 'image':
      return { id, type, src: '', alt: '', caption: '' };
    case 'file':
      return { id, type, href: '', label: 'Файл' };
    case 'link':
      return { id, type, href: '', label: 'Ссылка' };
    case 'table':
      return { id, type, rows: [['', ''], ['', '']] };
  }
}

function renderBlocks(blocks?: ContentBlock[], fallbackHtml = '') {
  if (!blocks?.length) {
    return <section className="method-content" dangerouslySetInnerHTML={{ __html: protectCodeBlocks(fallbackHtml) }} />;
  }

  return (
    <section className="method-content">
      {blocks.map((block) => <ContentBlockView block={block} key={block.id} />)}
    </section>
  );
}

function ContentBlockView({ block }: { block: ContentBlock }) {
  switch (block.type) {
    case 'heading': {
      const HeadingTag = `h${block.level || 3}` as keyof JSX.IntrinsicElements;
      return <HeadingTag>{block.text}</HeadingTag>;
    }
    case 'text':
      return <p>{block.text}</p>;
    case 'description':
      return <blockquote>{block.text}</blockquote>;
    case 'list': {
      const ListTag = block.style === 'ol' ? 'ol' : 'ul';
      return <ListTag>{(block.items || []).map((item, index) => <li key={`${block.id}-${index}`}>{item}</li>)}</ListTag>;
    }
    case 'code':
      return <pre><code>{block.code}</code></pre>;
    case 'image':
      return (
        <figure>
          {block.src && <img src={block.src} alt={block.alt || ''} />}
          {block.caption && <figcaption>{block.caption}</figcaption>}
        </figure>
      );
    case 'file':
    case 'link':
      return <p><a href={block.href} target="_blank" rel="noopener noreferrer">{block.label || block.href}</a></p>;
    case 'table':
      return (
        <table>
          <tbody>
            {(block.rows || []).map((row, rowIndex) => (
              <tr key={`${block.id}-row-${rowIndex}`}>
                {row.map((cell, cellIndex) => <td key={`${block.id}-${rowIndex}-${cellIndex}`}>{cell}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      );
  }
}

function App() {
  const [course, setCourse] = useState<CourseData>(seedCourse);
  const [isLoadingCourse, setIsLoadingCourse] = useState(true);
  const [activeModuleId, setActiveModuleId] = useState(seedCourse.modules[0].id);
  const [activeLessonId, setActiveLessonId] = useState(seedCourse.modules[0].lessons[0].id);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const isAdminPage = window.location.pathname.startsWith('/admin');

  useEffect(() => {
    fetch('/api/course')
      .then((response) => (response.ok ? response.json() : seedCourse))
      .then((loadedCourse: CourseData) => {
        setCourse(loadedCourse);
        setActiveModuleId(loadedCourse.modules[0]?.id || seedCourse.modules[0].id);
        setActiveLessonId(loadedCourse.modules[0]?.lessons[0]?.id || seedCourse.modules[0].lessons[0].id);
      })
      .catch(() => setCourse(seedCourse))
      .finally(() => setIsLoadingCourse(false));
  }, []);

  const activeModule = useMemo(
    () => course.modules.find((module) => module.id === activeModuleId) || course.modules[0],
    [activeModuleId, course],
  );

  const activeLesson = useMemo(
    () => activeModule.lessons.find((lesson) => lesson.id === activeLessonId) || activeModule.lessons[0],
    [activeLessonId, activeModule],
  );

  const selectModule = (module: Module) => {
    setActiveModuleId(module.id);
    setActiveLessonId(module.lessons[0].id);
  };

  if (isLoadingCourse) {
    return <main className="loading-page">Загрузка методички...</main>;
  }

  if (isAdminPage) {
    return <AdminPanel course={course} onCourseSaved={setCourse} />;
  }

  return (
    <main className={isSidebarOpen ? 'app-shell' : 'app-shell app-shell--sidebar-hidden'}>
      {!isSidebarOpen && (
        <button
          className="sidebar-toggle sidebar-toggle--floating"
          type="button"
          aria-label="Открыть меню модулей и заданий"
          aria-expanded={false}
          onClick={() => setIsSidebarOpen(true)}
        >
          <span aria-hidden="true">☰</span>
          <span>Меню</span>
        </button>
      )}

      {isSidebarOpen && (
        <aside className="sidebar" aria-label="Навигация по методичке">
          <div className="sidebar-actions">
            <button
              className="sidebar-toggle"
              type="button"
              aria-label="Скрыть меню модулей и заданий"
              aria-expanded={true}
              onClick={() => setIsSidebarOpen(false)}
            >
              <span aria-hidden="true">×</span>
              <span>Скрыть</span>
            </button>
          </div>

          <div className="brand">
            <span className="brand__mark">DEMO2026</span>
            <h1>{course.title}</h1>
            <p>{course.subtitle}</p>
          </div>

          <nav className="module-list">
            {course.modules.map((module) => (
              <section className="module-group" key={module.id}>
                <button
                  className={module.id === activeModule.id ? 'module-button module-button--active' : 'module-button'}
                  type="button"
                  onClick={() => selectModule(module)}
                >
                  {module.title}
                </button>

                {module.id === activeModule.id && (
                  <div className="lesson-list">
                    {module.lessons.map((lesson) => (
                      <button
                        className={lesson.id === activeLesson.id ? 'lesson-button lesson-button--active' : 'lesson-button'}
                        key={lesson.id}
                        type="button"
                        onClick={() => setActiveLessonId(lesson.id)}
                      >
                        {lesson.title}
                      </button>
                    ))}
                  </div>
                )}
              </section>
            ))}
          </nav>
        </aside>
      )}

      <section className="reader">
        <header className="reader-header">
          <div>
            <p>{activeModule.title}</p>
            <h2>{activeLesson.title}</h2>
          </div>
        </header>

        <article className="content-card">
          <section className="method-content method-content--intro">
            {(activeModule.introBlocks?.length ? activeModule.introBlocks : []).map((block) => (
              <ContentBlockView block={block} key={block.id} />
            ))}
            {!activeModule.introBlocks?.length && (
              <div dangerouslySetInnerHTML={{ __html: protectCodeBlocks(activeModule.introHtml) }} />
            )}
          </section>
          {renderBlocks(activeLesson.blocks, activeLesson.html)}
        </article>
      </section>
    </main>
  );
}

function AdminPanel({ course, onCourseSaved }: { course: CourseData; onCourseSaved: (course: CourseData) => void }) {
  const [token, setToken] = useState(() => localStorage.getItem('adminToken') || '');
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [draft, setDraft] = useState<CourseData>(() => cloneCourse(course));
  const [selectedModuleId, setSelectedModuleId] = useState(course.modules[0]?.id || '');
  const [selectedLessonId, setSelectedLessonId] = useState(course.modules[0]?.lessons[0]?.id || '');
  const [status, setStatus] = useState('');

  useEffect(() => {
    setDraft(cloneCourse(course));
    setSelectedModuleId(course.modules[0]?.id || '');
    setSelectedLessonId(course.modules[0]?.lessons[0]?.id || '');
  }, [course]);

  const selectedModule = draft.modules.find((module) => module.id === selectedModuleId) || draft.modules[0];
  const selectedLesson = selectedModule?.lessons.find((lesson) => lesson.id === selectedLessonId) || selectedModule?.lessons[0];

  const updateDraft = (updater: (nextCourse: CourseData) => void) => {
    setDraft((current) => {
      const next = cloneCourse(current);
      updater(next);
      return next;
    });
  };

  const login = async (event: FormEvent) => {
    event.preventDefault();
    setStatus('Проверяю логин...');
    const response = await fetch('/api/admin/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      setStatus('Неверный логин или пароль');
      return;
    }

    const data = await response.json();
    localStorage.setItem('adminToken', data.token);
    setToken(data.token);
    setPassword('');
    setStatus('Вход выполнен');
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    setToken('');
    setStatus('');
  };

  const saveCourse = async () => {
    setStatus('Сохраняю...');
    const preparedCourse = syncHtmlFields(draft);
    const response = await fetch('/api/admin/course', {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(preparedCourse),
    });

    if (!response.ok) {
      setStatus('Не удалось сохранить. Проверь логин.');
      return;
    }

    const savedCourse = await response.json();
    onCourseSaved(savedCourse);
    setDraft(cloneCourse(savedCourse));
    setStatus('Сохранено. Изменения уже видны на сайте.');
  };

  const addModule = () => {
    const nextNumber = draft.modules.length + 1;
    const module: Module = {
      id: `module-${Date.now()}`,
      title: `Модуль ${nextNumber}. Новый модуль`,
      introHtml: '',
      introBlocks: [createBlock('text')],
      lessons: [{
        id: `lesson-${Date.now()}`,
        title: '1. Новый пункт',
        html: '',
        blocks: [createBlock('text')],
      }],
    };
    updateDraft((next) => { next.modules.push(module); });
    setSelectedModuleId(module.id);
    setSelectedLessonId(module.lessons[0].id);
  };

  const deleteModule = () => {
    if (!selectedModule || !window.confirm(`Удалить модуль "${selectedModule.title}"?`)) {
      return;
    }
    updateDraft((next) => {
      next.modules = next.modules.filter((module) => module.id !== selectedModule.id);
      const first = next.modules[0];
      setSelectedModuleId(first?.id || '');
      setSelectedLessonId(first?.lessons[0]?.id || '');
    });
  };

  const addLesson = () => {
    if (!selectedModule) {
      return;
    }

    const lesson = {
      id: `${selectedModule.id}-custom-${Date.now()}`,
      title: `${selectedModule.lessons.length + 1}. Новый пункт`,
      html: '',
      blocks: [createBlock('text')],
    };

    updateDraft((next) => {
      const module = next.modules.find((item) => item.id === selectedModule.id);
      module?.lessons.push(lesson);
    });
    setSelectedLessonId(lesson.id);
  };

  const deleteLesson = () => {
    if (!selectedModule || !selectedLesson || !window.confirm(`Удалить пункт "${selectedLesson.title}"?`)) {
      return;
    }
    updateDraft((next) => {
      const module = next.modules.find((item) => item.id === selectedModule.id);
      if (!module) {
        return;
      }
      module.lessons = module.lessons.filter((lesson) => lesson.id !== selectedLesson.id);
      setSelectedLessonId(module.lessons[0]?.id || '');
    });
  };

  const uploadFile = async (file: File) => {
    const body = new FormData();
    body.append('file', file);
    const response = await fetch('/api/admin/upload', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body,
    });

    if (!response.ok) {
      throw new Error('Не удалось загрузить файл');
    }

    return response.json() as Promise<{ name: string; url: string }>;
  };

  if (!token) {
    return (
      <main className="admin-page admin-page--login">
        <form className="admin-login" onSubmit={login}>
          <span className="brand__mark">DEMO2026</span>
          <h1>Админка методички</h1>
          <label>
            Логин
            <input value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" />
          </label>
          <label>
            Пароль
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
            />
          </label>
          <button type="submit">Войти</button>
          {status && <p>{status}</p>}
        </form>
      </main>
    );
  }

  return (
    <main className="admin-page">
      <header className="admin-header">
        <div>
          <span className="brand__mark">DEMO2026</span>
          <h1>Админка методички</h1>
          <p>Редактирование через поля и блоки. HTML вводить не нужно.</p>
        </div>
        <div className="admin-header__actions">
          <a href="/">Открыть сайт</a>
          <button type="button" onClick={logout}>Выйти</button>
        </div>
      </header>

      <section className="admin-layout">
        <aside className="admin-list">
          <label>
            Название курса
            <input value={draft.title} onChange={(event) => updateDraft((next) => { next.title = event.target.value; })} />
          </label>
          <label>
            Подзаголовок
            <textarea value={draft.subtitle} onChange={(event) => updateDraft((next) => { next.subtitle = event.target.value; })} />
          </label>

          <div className="admin-button-row">
            <button className="admin-secondary" type="button" onClick={addModule}>Добавить модуль</button>
            <button className="admin-danger" type="button" onClick={deleteModule} disabled={!selectedModule}>Удалить модуль</button>
          </div>

          {draft.modules.map((module) => (
            <section className="admin-module" key={module.id}>
              <button
                className={module.id === selectedModule?.id ? 'module-button module-button--active' : 'module-button'}
                type="button"
                onClick={() => {
                  setSelectedModuleId(module.id);
                  setSelectedLessonId(module.lessons[0]?.id || '');
                }}
              >
                {module.title}
              </button>
              {module.id === selectedModule?.id && (
                <div className="lesson-list">
                  {module.lessons.map((lesson) => (
                    <button
                      className={lesson.id === selectedLesson?.id ? 'lesson-button lesson-button--active' : 'lesson-button'}
                      key={lesson.id}
                      type="button"
                      onClick={() => setSelectedLessonId(lesson.id)}
                    >
                      {lesson.title}
                    </button>
                  ))}
                </div>
              )}
            </section>
          ))}

          <div className="admin-button-row">
            <button className="admin-secondary" type="button" onClick={addLesson} disabled={!selectedModule}>Добавить пункт</button>
            <button className="admin-danger" type="button" onClick={deleteLesson} disabled={!selectedLesson}>Удалить пункт</button>
          </div>
        </aside>

        {selectedModule && selectedLesson && (
          <section className="admin-editor">
            <label>
              Название модуля
              <input
                value={selectedModule.title}
                onChange={(event) => updateDraft((next) => {
                  const module = next.modules.find((item) => item.id === selectedModule.id);
                  if (module) {
                    module.title = event.target.value;
                  }
                })}
              />
            </label>
            <BlockEditor
              title="Вступление модуля"
              blocks={selectedModule.introBlocks || []}
              onChange={(blocks) => updateDraft((next) => {
                const module = next.modules.find((item) => item.id === selectedModule.id);
                if (module) {
                  module.introBlocks = blocks;
                }
              })}
              uploadFile={uploadFile}
            />
            <label>
              Название пункта
              <input
                value={selectedLesson.title}
                onChange={(event) => updateDraft((next) => {
                  const module = next.modules.find((item) => item.id === selectedModule.id);
                  const lesson = module?.lessons.find((item) => item.id === selectedLesson.id);
                  if (lesson) {
                    lesson.title = event.target.value;
                  }
                })}
              />
            </label>
            <BlockEditor
              title="Содержимое пункта"
              blocks={selectedLesson.blocks || []}
              onChange={(blocks) => updateDraft((next) => {
                const module = next.modules.find((item) => item.id === selectedModule.id);
                const lesson = module?.lessons.find((item) => item.id === selectedLesson.id);
                if (lesson) {
                  lesson.blocks = blocks;
                }
              })}
              uploadFile={uploadFile}
            />
            <div className="admin-file-row">
              <button type="button" onClick={saveCourse}>Сохранить изменения</button>
            </div>
            {status && <p className="admin-status">{status}</p>}
          </section>
        )}
      </section>
    </main>
  );
}

function BlockEditor({
  title,
  blocks,
  onChange,
  uploadFile,
}: {
  title: string;
  blocks: ContentBlock[];
  onChange: (blocks: ContentBlock[]) => void;
  uploadFile: (file: File) => Promise<{ name: string; url: string }>;
}) {
  const updateBlock = (blockIdValue: string, patch: Partial<ContentBlock>) => {
    onChange(blocks.map((block) => (block.id === blockIdValue ? { ...block, ...patch } : block)));
  };

  const addBlock = (type: BlockType) => {
    onChange([...blocks, createBlock(type)]);
  };

  const deleteBlock = (block: ContentBlock) => {
    if (window.confirm(`Удалить блок "${blockLabels[block.type]}"?`)) {
      onChange(blocks.filter((item) => item.id !== block.id));
    }
  };

  const moveBlock = (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= blocks.length) {
      return;
    }
    const next = [...blocks];
    const [block] = next.splice(index, 1);
    next.splice(nextIndex, 0, block);
    onChange(next);
  };

  return (
    <section className="block-editor">
      <div className="block-editor__header">
        <h2>{title}</h2>
        <select defaultValue="" onChange={(event) => {
          if (event.target.value) {
            addBlock(event.target.value as BlockType);
            event.target.value = '';
          }
        }}>
          <option value="" disabled>Добавить блок</option>
          {(Object.keys(blockLabels) as BlockType[]).map((type) => <option key={type} value={type}>{blockLabels[type]}</option>)}
        </select>
      </div>

      <div className="block-list">
        {blocks.map((block, index) => (
          <article className="block-card" key={block.id}>
            <header className="block-card__header">
              <strong>{index + 1}. {blockLabels[block.type]}</strong>
              <div>
                <button type="button" onClick={() => moveBlock(index, -1)} disabled={index === 0}>↑</button>
                <button type="button" onClick={() => moveBlock(index, 1)} disabled={index === blocks.length - 1}>↓</button>
                <button type="button" className="admin-danger" onClick={() => deleteBlock(block)}>Удалить</button>
              </div>
            </header>
            <BlockFields block={block} updateBlock={updateBlock} uploadFile={uploadFile} />
          </article>
        ))}
      </div>
    </section>
  );
}

function BlockFields({
  block,
  updateBlock,
  uploadFile,
}: {
  block: ContentBlock;
  updateBlock: (id: string, patch: Partial<ContentBlock>) => void;
  uploadFile: (file: File) => Promise<{ name: string; url: string }>;
}) {
  const uploadIntoBlock = async (event: ChangeEvent<HTMLInputElement>, asImage: boolean) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    const uploaded = await uploadFile(file);
    updateBlock(block.id, asImage
      ? { src: uploaded.url, alt: block.alt || uploaded.name, caption: block.caption || uploaded.name }
      : { href: uploaded.url, label: block.label || uploaded.name });
  };

  if (block.type === 'heading') {
    return (
      <div className="block-fields block-fields--inline">
        <label>
          Уровень
          <select value={block.level || 3} onChange={(event) => updateBlock(block.id, { level: Number(event.target.value) as 2 | 3 | 4 })}>
            <option value={2}>H2</option>
            <option value={3}>H3</option>
            <option value={4}>H4</option>
          </select>
        </label>
        <label>
          Текст
          <input value={block.text || ''} onChange={(event) => updateBlock(block.id, { text: event.target.value })} />
        </label>
      </div>
    );
  }

  if (block.type === 'text' || block.type === 'description') {
    return (
      <label>
        Текст
        <textarea value={block.text || ''} onChange={(event) => updateBlock(block.id, { text: event.target.value })} />
      </label>
    );
  }

  if (block.type === 'list') {
    return (
      <div className="block-fields">
        <label>
          Вид списка
          <select value={block.style || 'ul'} onChange={(event) => updateBlock(block.id, { style: event.target.value as 'ul' | 'ol' })}>
            <option value="ul">Маркированный</option>
            <option value="ol">Нумерованный</option>
          </select>
        </label>
        <label>
          Пункты, каждый с новой строки
          <textarea
            value={(block.items || []).join('\n')}
            onChange={(event) => updateBlock(block.id, { items: event.target.value.split('\n') })}
          />
        </label>
      </div>
    );
  }

  if (block.type === 'code') {
    return (
      <label>
        Команды или код
        <textarea className="admin-code-editor" value={block.code || ''} onChange={(event) => updateBlock(block.id, { code: event.target.value })} />
      </label>
    );
  }

  if (block.type === 'image') {
    return (
      <div className="block-fields">
        <label>
          Изображение
          <input type="file" accept="image/*" onChange={(event) => uploadIntoBlock(event, true)} />
        </label>
        <label>
          Адрес изображения
          <input value={block.src || ''} onChange={(event) => updateBlock(block.id, { src: event.target.value })} />
        </label>
        <label>
          Alt
          <input value={block.alt || ''} onChange={(event) => updateBlock(block.id, { alt: event.target.value })} />
        </label>
        <label>
          Подпись
          <input value={block.caption || ''} onChange={(event) => updateBlock(block.id, { caption: event.target.value })} />
        </label>
      </div>
    );
  }

  if (block.type === 'file' || block.type === 'link') {
    return (
      <div className="block-fields">
        {block.type === 'file' && (
          <label>
            Загрузить файл
            <input type="file" onChange={(event) => uploadIntoBlock(event, false)} />
          </label>
        )}
        <label>
          Текст
          <input value={block.label || ''} onChange={(event) => updateBlock(block.id, { label: event.target.value })} />
        </label>
        <label>
          Адрес
          <input value={block.href || ''} onChange={(event) => updateBlock(block.id, { href: event.target.value })} />
        </label>
      </div>
    );
  }

  if (block.type === 'table') {
    const rows = block.rows?.length ? block.rows : [['']];
    const updateCell = (rowIndex: number, cellIndex: number, value: string) => {
      const nextRows = rows.map((row) => [...row]);
      nextRows[rowIndex][cellIndex] = value;
      updateBlock(block.id, { rows: nextRows });
    };
    const addRow = () => updateBlock(block.id, { rows: [...rows, Array(rows[0]?.length || 1).fill('')] });
    const addColumn = () => updateBlock(block.id, { rows: rows.map((row) => [...row, '']) });

    return (
      <div className="admin-table-editor">
        <div className="admin-button-row">
          <button type="button" onClick={addRow}>Добавить строку</button>
          <button type="button" onClick={addColumn}>Добавить колонку</button>
        </div>
        <table>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={`${block.id}-edit-row-${rowIndex}`}>
                {row.map((cell, cellIndex) => (
                  <td key={`${block.id}-edit-cell-${rowIndex}-${cellIndex}`}>
                    <input value={cell} onChange={(event) => updateCell(rowIndex, cellIndex, event.target.value)} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return null;
}

export default App;
